import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Search,
  Trash2,
  Plus,
  Printer,
  Eraser,
  Barcode,
  LayoutGrid,
  AlertTriangle,
  Package,
} from "lucide-react";
import BarcodePrintDialog from "@/components/barcode/BarcodePrintDialog";

const LABELS_PER_SET = 5;

/**
 * BarcodePrintingTab — Stock page → "Barcode Printing" tab.
 *
 * Lets the user search for products, build a queue of {product, qty} rows,
 * see a live print summary, and hand the queue off to the existing
 * BarcodePrintDialog for the actual print (bridge probe, BarTender, etc.).
 *
 * Reuses, NEVER replaces, the existing printing pipeline. All bridge
 * connection, CSV generation, BarTender invocation, test print and
 * settings UI live inside BarcodePrintDialog and are invoked unchanged.
 */
export default function BarcodePrintingTab() {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [rows, setRows] = useState([]); // [{ product_id, product_name, auto_barcode, mrp, default_price, quantity }]
  const [printOpen, setPrintOpen] = useState(false);
  const searchRef = useRef(null);

  // Debounced product search — mirrors the Stock tab's behaviour.
  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/products", { params: { q, limit: 12 } });
      setResults(data.items || []);
      setShowResults(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  // Add a product to the print queue (or bump its quantity if already there).
  const addProduct = (p) => {
    if (!p?.auto_barcode) {
      toast.error("This product has no auto-barcode — cannot print a label.");
      return;
    }
    setRows((prev) => {
      const i = prev.findIndex((r) => r.product_id === p.id);
      if (i >= 0) {
        const next = prev.slice();
        next[i] = { ...next[i], quantity: (parseInt(next[i].quantity, 10) || 0) + 1 };
        return next;
      }
      return [
        ...prev,
        {
          product_id: p.id,
          product_name: p.product,
          auto_barcode: p.auto_barcode,
          mrp: p.mrp ?? "",
          default_price: p.default_price ?? "",
          quantity: 1,
        },
      ];
    });
    setQuery("");
    setResults([]);
    setShowResults(false);
    searchRef.current?.focus();
  };

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const clearAll = () => setRows([]);

  const handleQtyChange = (idx, raw) => {
    if (raw === "") { updateRow(idx, { quantity: "" }); return; }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Quantity must be a positive integer");
      return;
    }
    updateRow(idx, { quantity: n });
  };

  // Live summary — same maths as the dialog so the numbers don't drift.
  const printableRows = useMemo(
    () => rows.filter((r) => Number(r.quantity) > 0),
    [rows],
  );
  const totalLabels = useMemo(
    () => printableRows.reduce((acc, r) => acc + (parseInt(r.quantity, 10) || 0), 0),
    [printableRows],
  );
  const productsSelected = printableRows.length;
  const fullSets = Math.floor(totalLabels / LABELS_PER_SET);
  const partialLabels = totalLabels % LABELS_PER_SET;
  const totalSetsRequired = Math.ceil(totalLabels / LABELS_PER_SET);

  const handlePrint = () => {
    if (printableRows.length === 0) {
      toast.error("Add at least one product with quantity ≥ 1");
      return;
    }
    setPrintOpen(true);
  };

  // Products handed to the dialog already carry the user's qty per row.
  const sourceProducts = useMemo(
    () => printableRows.map((r) => ({
      id: r.product_id,
      product: r.product_name,
      auto_barcode: r.auto_barcode,
      mrp: r.mrp,
      default_price: r.default_price,
      quantity: r.quantity,
    })),
    [printableRows],
  );

  return (
    <div data-testid="barcode-printing-tab">
      {/* ===== Search box ===== */}
      <Card className="p-3 sm:p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            className="pl-10"
            placeholder="Search by product, auto barcode, manual barcode, brand or group..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setShowResults(true)}
            data-testid="bp-search-input"
          />
          {searching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              Searching…
            </div>
          )}

          {/* Results dropdown */}
          {showResults && results.length > 0 && (
            <Card
              className="absolute z-50 w-full mt-1 max-h-72 overflow-y-auto shadow-lg"
              data-testid="bp-search-results"
            >
              {results.map((p) => (
                <button
                  key={p.id}
                  className="w-full p-3 text-left hover:bg-muted border-b last:border-b-0 transition-colors flex items-center gap-3"
                  onClick={() => addProduct(p)}
                  data-testid={`bp-result-${p.id}`}
                >
                  <Plus className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{p.product}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.brand_name} · {p.auto_barcode}
                      {p.manual_barcode && ` · ${p.manual_barcode}`}
                      {p.mrp ? ` · MRP ₹${Number(p.mrp).toFixed(2)}` : ""}
                    </div>
                  </div>
                </button>
              ))}
            </Card>
          )}
        </div>
      </Card>

      {/* ===== Selected list + summary ===== */}
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        {/* ---- Left: rows table ---- */}
        <Card className="overflow-hidden" data-testid="bp-rows-card">
          <div className="px-4 py-2 border-b bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
            <span>Products to print ({rows.length})</span>
            {rows.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="h-7 text-xs text-destructive hover:text-destructive"
                data-testid="bp-clear-all"
              >
                <Eraser className="w-3.5 h-3.5 mr-1" /> Clear All
              </Button>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="bp-empty-state">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Search above and click a product to add it to the print queue.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="bp-rows-table">
                <thead className="bg-white">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                    <th className="p-2 font-medium">Product</th>
                    <th className="p-2 font-medium">Barcode</th>
                    <th className="p-2 font-medium text-right w-[90px]">MRP</th>
                    <th className="p-2 font-medium text-right w-[90px]">DP</th>
                    <th className="p-2 font-medium text-right w-[90px]">Labels Qty</th>
                    <th className="p-2 w-[40px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr
                      key={`${r.product_id}-${idx}`}
                      className="border-t align-middle"
                      data-testid={`bp-row-${idx}`}
                    >
                      <td className="p-2 max-w-[280px]">
                        <div className="truncate font-medium" title={r.product_name}>
                          {r.product_name || "—"}
                        </div>
                      </td>
                      <td className="p-2 font-mono-data text-muted-foreground">
                        {r.auto_barcode || "—"}
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" step="0.01" min={0}
                          value={r.mrp ?? ""}
                          onChange={(e) => updateRow(idx, { mrp: e.target.value === "" ? "" : Number(e.target.value) })}
                          className="h-7 text-xs text-right font-mono-data"
                          data-testid={`bp-mrp-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" step="0.01" min={0}
                          value={r.default_price ?? ""}
                          onChange={(e) => updateRow(idx, { default_price: e.target.value === "" ? "" : Number(e.target.value) })}
                          className="h-7 text-xs text-right font-mono-data"
                          data-testid={`bp-dp-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" min={0} step={1}
                          value={r.quantity}
                          onChange={(e) => handleQtyChange(idx, e.target.value)}
                          className="h-7 text-xs text-right font-mono-data"
                          data-testid={`bp-qty-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => removeRow(idx)}
                          className="h-7 w-7 p-0 text-destructive"
                          title="Remove from print list"
                          data-testid={`bp-remove-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ---- Right: Print summary ---- */}
        <Card className="overflow-hidden flex flex-col" data-testid="bp-summary-card">
          <div className="px-4 py-2 border-b bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <LayoutGrid className="w-3.5 h-3.5" /> Print Summary
          </div>
          <div className="p-4 grid grid-cols-2 gap-x-4 gap-y-3 bg-white">
            <SummaryCell label="Products Selected" value={productsSelected} testid="bp-sum-products" />
            <SummaryCell label="Total Labels" value={totalLabels} testid="bp-sum-total-labels" emphasis />
            <SummaryCell label="Labels per Set" value={LABELS_PER_SET} testid="bp-sum-labels-per-set" muted />
            <SummaryCell label="Full Sets" value={fullSets} testid="bp-sum-full-sets" />
            <SummaryCell label="Partial Labels" value={partialLabels} testid="bp-sum-partial" />
            <SummaryCell label="Total Sets Required" value={totalSetsRequired} testid="bp-sum-sets-required" emphasis />
          </div>

          {partialLabels > 0 && (
            <div
              className="mx-4 mb-3 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
              data-testid="bp-partial-note"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                The final set uses only <strong>{partialLabels}</strong> label
                {partialLabels === 1 ? "" : "s"}. The remaining{" "}
                <strong>{LABELS_PER_SET - partialLabels}</strong> label
                {LABELS_PER_SET - partialLabels === 1 ? "" : "s"} in that section may remain unused.
              </span>
            </div>
          )}

          {/* Footer action: Print → opens existing BarcodePrintDialog with the queue. */}
          <div className="mt-auto border-t bg-muted/10 px-4 py-3 flex items-center justify-end gap-2">
            <Button
              onClick={handlePrint}
              disabled={totalLabels === 0}
              data-testid="bp-print-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {totalLabels === 0
                ? "Print Barcode Labels"
                : `Print ${totalLabels} Label${totalLabels === 1 ? "" : "s"}`}
            </Button>
          </div>
        </Card>
      </div>

      {/* ===== Existing dialog — drives the entire BarTender pipeline ===== */}
      <BarcodePrintDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        sourceProducts={sourceProducts}
        defaultQuantityKey="quantity"
        title="Print Barcode Labels"
      />
    </div>
  );
}

function SummaryCell({ label, value, testid, emphasis, muted }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span
        className={`font-mono-data mt-0.5 ${
          emphasis
            ? "text-xl font-semibold text-foreground"
            : muted
              ? "text-base text-muted-foreground"
              : "text-base font-medium text-foreground"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

// Re-export the Barcode icon so this module can be a single-import use.
export { Barcode };
